import React, { useContext, useEffect, useRef, useState } from 'react';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { ValidationError } from 'yup';
import ValidationDiv from '@utils/validation';
import { LovDTO, Pageable } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api.tsx';
import { PagedData } from '@components/RingilDataTable/RingilDataTable.tsx';
import { deferStampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';
import { useTranslation } from 'react-i18next';
import { ExpeditionDetailHandlerContext } from '@app/pages/carrier/expedition-detail/ExpeditionDetailHandler.tsx';
import useDispatchers from '@hooks/dispatchers/dispatchers.tsx';
import Label from '@components/obsolete/Form/Label/Label.tsx';
import EditableDropdown, { DropdownItem } from '@components/RingilComponents/EditableDropdown.tsx';
import styled from 'styled-components';

interface ComponentProps {
  disabled?: boolean;
  validationErrors: ValidationError[];
}

const AssignVehicleComponent: React.FC<ComponentProps> = ({ disabled, validationErrors }) => {
  const { t } = useTranslation('common');
  const {
    state,
    changePlate,
    changeDispatcher,
    changeDriver,
  } = useContext(ExpeditionDetailHandlerContext);
  const {
    listCompanyUsersDrivers,
    companyUserDriverSetDeleted,
    createCompanyUserDriver,
    listCarPlates,
    deleteCarPlate,
  } = useContext(ApiContext);
  const [selectedOptions, setSelectedOptions] = useState<DropdownItem[]>([]);

  const dispatchers = useDispatchers(state.header.id);
  const dispatcherDropdownRef = useRef<HTMLDivElement | null>(null);

  const loadCarPlatesFromApi = (
    filter: string,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<DropdownItem>) => void,
  ) => {
    listCarPlates(data => {
      const mapped: PagedData<DropdownItem> = {
        data: data.map(plate => ({ id: plate.id, value: plate.number })),
        total: (data as any).total ?? data.length,
        page: (data as any).page ?? 0,
        size: (data as any).size ?? data.length,
      };
      onSuccess(mapped);
    });
  };

  const loadDriversFromApi = (
    filter: string,
    pageable: Pageable,
    onSuccess: (pagedData: PagedData<DropdownItem>) => void,
  ) => {
    listCompanyUsersDrivers(
      pageable,
      filter,
      data => {
        const mapped: PagedData<DropdownItem> = {
          data: data.data.map(driver => ({ id: driver.id, value: driver.name })),
          total: (data as any).total ?? data.data.length,
          page: (data as any).page ?? 0,
          size: (data as any).size ?? data.data.length,
        };
        onSuccess(mapped);
      },
    );
  };

  useEffect(() => {
    if (state.createReadyToShipment.carPlate) {
      const plateNumbers = state.createReadyToShipment.carPlate.split(' || ');
      const initialPlates = plateNumbers.map(plateNumber => ({
        id: plateNumber,
        value: plateNumber,
      }));
      setSelectedOptions(initialPlates);
    }
  }, [state.createReadyToShipment.carPlate]);

  useEffect(() => {
    return deferStampDataTestIdOnDescendantRef(
      dispatcherDropdownRef,
      '.p-dropdown',
      'shipmentProgress-dispatcherDropdown',
    );
  }, [dispatchers]);

  const handleSelectPlate = (plates: DropdownItem[]) => {
    changePlate(plates.map(p => p.value).join(' || '));
  };

  const handleDropDownDelete = (plate: DropdownItem, onDeleteSuccess: () => void) => {
    deleteCarPlate(plate.id, () => {
      setSelectedOptions(prevState => prevState.filter(p => p.id !== plate.id));
      onDeleteSuccess();
    });
  };

  const handleSelectDrivers = (drivers: DropdownItem[]) => {
    if (drivers.length > 0) {
      const driverToAdd = drivers[0];
      if (driverToAdd.isNew) {
        createCompanyUserDriver({ driverName: driverToAdd.value }, createdDriver => {
          changeDriver({ id: createdDriver.companyUserId, name: driverToAdd.value });
        });
      } else {
        changeDriver({ id: driverToAdd.id, name: driverToAdd.value });
      }
    }
  };

  const handleDeleteDriver = (driver: DropdownItem, onDeleteSuccess: () => void) => {
    if (driver.id) {
      companyUserDriverSetDeleted(driver.id, { isDeleted: true }, () => {
        if (state.createReadyToShipment?.driver?.id === driver.id) {
          changeDriver(null);
        }
        onDeleteSuccess();
      });
    }
  };

  return (
    <>
      <h4 className="p-mt-0" style={{ fontWeight: 'normal' }}>
        {t(`AssignVehicleComponent.assignAVehicle`, `Assign a vehicle`)}
      </h4>
      <div className="group-wrapper group-assign-vehicle">
        <div className="p-field">
          <EditableDropdown
            loadOptionsFromApi={loadCarPlatesFromApi}
            selectedOptions={selectedOptions}
            onSelect={(plates: DropdownItem[]) => handleSelectPlate(plates)}
            onDelete={(plate: DropdownItem, onDeleteSuccess) => handleDropDownDelete(plate, onDeleteSuccess)}
            placeholder="Search car plates..."
            label="SPZ"
            multiSelect
            mandatory
            dataTestId="shipmentProgress-carPlate"
            dataTestIdList="shipmentProgress-carPlate-list"
            dataTestIdItemPrefix="shipmentProgress-carPlate"
            note={
              <Note>
                <b>{t(`InputPlate.note1`, `Máte auto s přívěsem?`)}</b>
                <p>{t(`InputPlate.note2`, `Např. tahač SPZ 0001 | 1. přívěs SPZ 0002 | 2. přívěs SPZ 00003`)}</p>
                <p>{t(`InputPlate.note3`, `Oddělte SPZ čárkami: SPZ 0001, SPZ 0002. SPZ 0003`)}</p>
              </Note>
            }
          />
          <ValidationDiv errors={validationErrors} path={'carPlate'} />
        </div>
        <div className="p-field">
          <EditableDropdown
            loadOptionsFromApi={loadDriversFromApi}
            selectedOptions={state.createReadyToShipment.driver ? [{
              id: state.createReadyToShipment.driver.id,
              value: state.createReadyToShipment.driver.name,
            }] : []}
            onSelect={(drivers: DropdownItem[]) => handleSelectDrivers(drivers)}
            onDelete={(driver: DropdownItem, onDeleteSuccess) => handleDeleteDriver(driver, onDeleteSuccess)}
            placeholder={t('driverDropdown.placeholder', 'Search drivers...')}
            label={t('driverDropdown.label', 'Driver')}
            mandatory
            dataTestId="shipmentProgress-driverDropdown"
            dataTestIdList="shipmentProgress-driverDropdown-list"
            dataTestIdItemPrefix="shipmentProgress-driverDropdown"
          />
          <ValidationDiv errors={validationErrors} path={'driver'} />
          <ValidationDiv errors={validationErrors} path={'driver.id'} />
        </div>
        <div className="p-field">
          <Label title={t(`AssignVehicleComponent.dispatcher`, `Dispatcher`)} />
          <div ref={dispatcherDropdownRef}>
            <Dropdown
              options={dispatchers}
              optionValue="id"
              onChange={e => changeDispatcher(e.value)}
              optionLabel="name"
              filter={true}
              value={state.createReadyToShipment.dispatcherId}
              disabled={disabled}
              itemTemplate={(option: LovDTO) => (
                <span data-testid={`shipmentProgress-dispatcherDropdown-${toTestIdSlug(option.name)}`}>
                  {option.name}
                </span>
              )}
            />
          </div>
          <ValidationDiv errors={validationErrors} path={'dispatcherId'} />
        </div>
      </div>
    </>
  );
};

export default AssignVehicleComponent;

const Note = styled.div`
  display: flex;
  flex-direction: column;
  font-size: 0.78rem;

  b {
    font-weight: 700;
    color: black;
    margin-top: 0.3rem;
  }

  p {
    margin: 0;
  }
`;